import { useEffect, useMemo } from 'react';

import { DocumentDetails_DocumentFragment } from '@/modules/documents/graphql/DocumentDetails.generated';

import { useDocumentDetailsPageLoadingState } from './useDocumentDetailsPageLoadingState';

interface UsePageLoadingBridgeProps {
  document: DocumentDetails_DocumentFragment | null;
  queryLoading: boolean;
  isGenerating: boolean;
}

/**
 * Bridges the new hook-based architecture with the existing loading state reducer.
 * Combines document, query, and generation states into the format expected by the
 * legacy useDocumentDetailsPageLoadingState reducer. Returns computed loading states
 * for the About section and Summary section.
 */
export function usePageLoadingBridge({
  document,
  queryLoading,
  isGenerating,
}: UsePageLoadingBridgeProps) {
  const {
    state: {
      error,
      about: { loading: aboutLoadingState },
      summary: { loading: summaryLoadingState },
    },
    dispatch,
  } = useDocumentDetailsPageLoadingState();

  const combinedState = useMemo(
    () => ({
      doneLoading: !queryLoading,
      hasDocument: Boolean(document),
      hasSummary: Boolean(document?.summaries?.[0]),
      isGeneratingAiSummary: isGenerating,
    }),
    [queryLoading, document, isGenerating]
  );

  useEffect(() => {
    dispatch({ type: 'stateUpdate', input: combinedState });
  }, [dispatch, combinedState]);

  return {
    aboutLoadingState,
    summaryLoadingState,
    error,
  };
}
